import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import './App.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/800.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/600.css';
import { 
  Sun, Zap, Battery, Shield, Award, Clock, ChevronRight, 
  Phone, Mail, MapPin, Menu, X, Star, ArrowRight, Users, Building2,
  Wrench, Calculator, FileText, LayoutDashboard, Package, FolderKanban,
  Settings, LogOut, Edit, Trash2, Plus, Eye, Download,
  Facebook, Instagram, Linkedin, MessageCircle, CheckCircle, Lightbulb,
  TrendingUp, Image, Quote
} from 'lucide-react';

// API Setup
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const api = axios.create({ baseURL: API_URL });

// ============ CONSTANTS ============
const SCROLL_THRESHOLD = 50;
const ANNUAL_GENERATION_PER_KW = 1500; // kWh per kW
const SYSTEM_COST_PER_KW = 55000; // INR per kW
const SYSTEM_LIFETIME_YEARS = 25;
const CO2_OFFSET_FACTOR = 0.82; // kg CO2 per kWh

// ============ PREMIUM IMAGES ============
const IMAGES = {
  logo: 'https://customer-assets.emergentagent.com/job_2b6c6d3a-ac9d-4a3b-b4d9-60c567ac4251/artifacts/njh5q07i_image.png',
  hero: 'https://images.unsplash.com/photo-1613665813446-82a78c468a1d?w=1920&q=80',
  solarField: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=80',
  rooftopSolar: 'https://images.unsplash.com/photo-1611365892117-00ac5ef43c90?w=1200&q=80',
  industrialPlant: 'https://images.unsplash.com/photo-1670689334799-cdc6777db8cc?w=1200&q=80',
  batteryStorage: 'https://images.unsplash.com/photo-1742899273038-67ff67477663?w=1200&q=80',
  commercial: 'https://images.unsplash.com/photo-1726866492047-7f9516558c6e?w=1200&q=80',
  engineers: 'https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=1200&q=80',
  about: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1200&q=80',
};

// ============ CONTACT INFO ============
const CONTACT = {
  salesPhone: '8805859200',
  escalationPhone: '8408022224',
  email1: 'sales@amroxenergy.com',
  email2: 'work@amrosolar.solutions',
  whatsapp: '918805859200',
};

// ============ NAVBAR ============
const Navbar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty deps is correct - handleScroll is defined inside useEffect

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <Link to="/" className="navbar-logo">
            <img src={IMAGES.logo} alt="AMROX Energy" style={{height: '55px', width: 'auto'}} />
          </Link>

          <div className="navbar-nav">
            <Link to="/" className={isActive('/') ? 'active' : ''}>Home</Link>
            <Link to="/about" className={isActive('/about') ? 'active' : ''}>About</Link>
            <Link to="/solutions" className={isActive('/solutions') ? 'active' : ''}>Solutions</Link>
            <Link to="/projects" className={isActive('/projects') ? 'active' : ''}>Projects</Link>
            <Link to="/calculator" className={isActive('/calculator') ? 'active' : ''}>Calculator</Link>
            <Link to="/contact" className={isActive('/contact') ? 'active' : ''}>Contact</Link>
          </div>

          <div className="navbar-cta">
            <a href={`tel:${CONTACT.salesPhone}`} className="navbar-phone hide-mobile">
              <Phone size={18} />
              <span>{CONTACT.salesPhone}</span>
            </a>
            <Link to="/contact" className="btn btn-primary btn-sm hide-mobile">
              Get Quote
            </Link>
            <button 
              className="mobile-menu-btn" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
        <Link to="/about" onClick={() => setMobileMenuOpen(false)}>About</Link>
        <Link to="/solutions" onClick={() => setMobileMenuOpen(false)}>Solutions</Link>
        <Link to="/projects" onClick={() => setMobileMenuOpen(false)}>Projects</Link>
        <Link to="/calculator" onClick={() => setMobileMenuOpen(false)}>Solar Calculator</Link>
        <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
        <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
        <a href={`tel:${CONTACT.salesPhone}`} className="text-gold" onClick={() => setMobileMenuOpen(false)}>
          <Phone size={18} /> Sales: {CONTACT.salesPhone}
        </a>
        <a href={`tel:${CONTACT.escalationPhone}`} className="text-gold" onClick={() => setMobileMenuOpen(false)}>
          <Phone size={18} /> Escalation: {CONTACT.escalationPhone}
        </a>
      </div>
    </>
  );
};

// ============ WHATSAPP BUTTON ============
const WhatsAppButton = () => (
  <div className="whatsapp-float">
    <a 
      href={`https://wa.me/${CONTACT.whatsapp}?text=Hello%20AMROX%20Energy,%20I%20am%20interested%20in%20solar%20solutions`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-btn"
    >
      <MessageCircle size={32} />
    </a>
  </div>
);

// ============ STICKY CTA ============
const StickyCTA = () => (
  <div className="sticky-cta hide-mobile">
    <a href={`tel:${CONTACT.salesPhone}`} className="btn btn-outline btn-sm">
      <Phone size={16} /> Talk to Expert
    </a>
    <Link to="/contact" className="btn btn-primary btn-sm">
      Get Free Site Survey
    </Link>
  </div>
);

// ============ FOOTER ============
const Footer = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-grid">
        <div className="footer-brand">
          <img src={IMAGES.logo} alt="AMROX Energy" style={{height: '60px', marginBottom: '16px'}} />
          <p>Powering Authority. Enabling Growth. Premium solar EPC solutions for residential, commercial, and industrial clients across Maharashtra.</p>
          <div className="footer-social">
            <a href="#" aria-label="Facebook"><Facebook size={20} /></a>
            <a href="#" aria-label="Instagram"><Instagram size={20} /></a>
            <a href="#" aria-label="LinkedIn"><Linkedin size={20} /></a>
          </div>
        </div>

        <div className="footer-column">
          <h5>Solutions</h5>
          <ul>
            <li><Link to="/solutions">Residential Solar</Link></li>
            <li><Link to="/solutions">Commercial Solar</Link></li>
            <li><Link to="/solutions">Industrial EPC</Link></li>
            <li><Link to="/solutions">Battery Storage</Link></li>
            <li><Link to="/solutions">Solar AMC</Link></li>
          </ul>
        </div>

        <div className="footer-column">
          <h5>Company</h5>
          <ul>
            <li><Link to="/about">About Us</Link></li>
            <li><Link to="/projects">Projects</Link></li>
            <li><Link to="/calculator">Savings Calculator</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li><Link to="/admin">Admin Portal</Link></li>
          </ul>
        </div>

        <div className="footer-column">
          <h5>Contact</h5>
          <ul>
            <li><a href={`tel:${CONTACT.salesPhone}`}><Phone size={14} /> Sales: {CONTACT.salesPhone}</a></li>
            <li><a href={`tel:${CONTACT.escalationPhone}`}><Phone size={14} /> Escalation: {CONTACT.escalationPhone}</a></li>
            <li><a href={`mailto:${CONTACT.email1}`}><Mail size={14} /> {CONTACT.email1}</a></li>
            <li><a href={`mailto:${CONTACT.email2}`}><Mail size={14} /> {CONTACT.email2}</a></li>
            <li><span><MapPin size={14} /> Pune, Maharashtra</span></li>
          </ul>
        </div>
      </div>

      <div className="footer-bottom">
        <p>&copy; 2025 AMROX ENERGY - Amro Power Systems Pvt Ltd. All rights reserved.</p>
        <div className="footer-legal">
          <a href="#">Privacy Policy</a>
          <a href="#">Terms of Service</a>
        </div>
      </div>
    </div>
  </footer>
);

// ============ HOMEPAGE ============
const HomePage = () => {
  const [stats, setStats] = useState({ products: 0, projects: 0, reviews: 0 });

  useEffect(() => {
    api.get('/api/admin/analytics').then(res => setStats(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="homepage">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <img src={IMAGES.hero} alt="Solar Installation" />
        </div>
        <div className="hero-overlay"></div>
        <div className="hero-grid-pattern"></div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <Zap size={16} />
            Premium Energy Infrastructure
          </div>
          
          <h1 className="hero-title">
            Powering <span className="gold">Smarter</span><br />Energy Futures.
          </h1>
          
          <p className="hero-subtitle">
            Residential • Commercial • Industrial Solar EPC Solutions
          </p>
          
          <div className="hero-cta">
            <Link to="/contact" className="btn btn-primary btn-lg" data-testid="hero-cta-survey">
              Get Free Site Survey <ArrowRight size={20} />
            </Link>
            <Link to="/calculator" className="btn btn-secondary btn-lg" data-testid="hero-cta-calculator">
              Calculate Your Savings
            </Link>
          </div>

          {/* Trust Counters */}
          <div className="trust-counters">
            <div className="trust-counter">
              <div className="trust-counter-value">12<span>+</span></div>
              <div className="trust-counter-label">MW Installed</div>
            </div>
            <div className="trust-counter">
              <div className="trust-counter-value">850<span>+</span></div>
              <div className="trust-counter-label">Projects Completed</div>
            </div>
            <div className="trust-counter">
              <div className="trust-counter-value">98<span>%</span></div>
              <div className="trust-counter-label">Client Satisfaction</div>
            </div>
            <div className="trust-counter">
              <div className="trust-counter-value">24/7</div>
              <div className="trust-counter-label">Technical Support</div>
            </div>
            <div className="trust-counter">
              <div className="trust-counter-value"><MapPin size={24} /></div>
              <div className="trust-counter-label">Across Maharashtra</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="about-grid">
            <div className="about-image">
              <img src={IMAGES.about} alt="AMROX Energy Team" />
            </div>
            <div className="about-content">
              <div className="section-badge"><Award size={14} /> About AMROX</div>
              <h2>Engineered for <span className="gold">Reliability</span></h2>
              <p>AMROX ENERGY is a premium energy infrastructure brand under Amro Power Systems Pvt Ltd, delivering intelligent, scalable, and performance-driven solar and power solutions.</p>
              <p>We specialize in end-to-end solar EPC services for residential, commercial, and industrial clients across Maharashtra, with a focus on quality, efficiency, and long-term value.</p>
              
              <div className="about-features">
                <div className="about-feature">
                  <div className="about-feature-icon"><Shield size={24} /></div>
                  <div>
                    <h5>Quality Assured</h5>
                    <p>Tier-1 components only</p>
                  </div>
                </div>
                <div className="about-feature">
                  <div className="about-feature-icon"><Clock size={24} /></div>
                  <div>
                    <h5>On-Time Delivery</h5>
                    <p>Committed timelines</p>
                  </div>
                </div>
                <div className="about-feature">
                  <div className="about-feature-icon"><Wrench size={24} /></div>
                  <div>
                    <h5>Expert Installation</h5>
                    <p>Certified engineers</p>
                  </div>
                </div>
                <div className="about-feature">
                  <div className="about-feature-icon"><Lightbulb size={24} /></div>
                  <div>
                    <h5>Smart Monitoring</h5>
                    <p>Real-time tracking</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="section section-darker">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><Star size={14} /> Why Choose Us</div>
            <h2 className="section-title">The AMROX <span className="gold">Advantage</span></h2>
            <p className="section-description">Built for performance. Engineered for reliability. Designed for your success.</p>
          </div>

          <div className="why-grid">
            <div className="why-card">
              <div className="why-card-icon"><Award size={36} /></div>
              <h4>Premium Quality</h4>
              <p>Only Tier-1 solar panels and inverters with industry-leading warranties.</p>
            </div>
            <div className="why-card">
              <div className="why-card-icon"><TrendingUp size={36} /></div>
              <h4>Maximum ROI</h4>
              <p>Optimized system design for highest energy generation and fastest payback.</p>
            </div>
            <div className="why-card">
              <div className="why-card-icon"><Users size={36} /></div>
              <h4>Expert Team</h4>
              <p>Certified engineers with 10+ years experience in solar EPC projects.</p>
            </div>
            <div className="why-card">
              <div className="why-card-icon"><Shield size={36} /></div>
              <h4>Complete Support</h4>
              <p>End-to-end service from design to installation to maintenance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><Sun size={14} /> Our Solutions</div>
            <h2 className="section-title">Smart Energy <span className="gold">Solutions</span></h2>
            <p className="section-description">Comprehensive solar and power solutions tailored to your needs.</p>
          </div>

          <div className="solutions-grid">
            <div className="solution-card">
              <div className="solution-card-image">
                <img src={IMAGES.rooftopSolar} alt="Residential Solar" />
                <span className="solution-card-badge">Residential</span>
              </div>
              <div className="solution-card-content">
                <h4>Residential Solar</h4>
                <p>Power your home with clean energy. Systems from 1kW to 10kW with government subsidy benefits.</p>
                <div className="solution-card-features">
                  <span className="solution-feature">Net Metering</span>
                  <span className="solution-feature">Subsidy</span>
                  <span className="solution-feature">25Y Warranty</span>
                </div>
                <Link to="/solutions" className="btn btn-outline btn-sm">Learn More <ChevronRight size={16} /></Link>
              </div>
            </div>

            <div className="solution-card">
              <div className="solution-card-image">
                <img src={IMAGES.commercial} alt="Commercial Solar" />
                <span className="solution-card-badge">Commercial</span>
              </div>
              <div className="solution-card-content">
                <h4>Commercial Solar EPC</h4>
                <p>Reduce operational costs with scalable solar solutions for offices, factories, and warehouses.</p>
                <div className="solution-card-features">
                  <span className="solution-feature">Turnkey EPC</span>
                  <span className="solution-feature">Tax Benefits</span>
                  <span className="solution-feature">AMC</span>
                </div>
                <Link to="/solutions" className="btn btn-outline btn-sm">Learn More <ChevronRight size={16} /></Link>
              </div>
            </div>

            <div className="solution-card">
              <div className="solution-card-image">
                <img src={IMAGES.batteryStorage} alt="Battery Storage" />
                <span className="solution-card-badge">Storage</span>
              </div>
              <div className="solution-card-content">
                <h4>Battery Storage Systems</h4>
                <p>Energy independence with advanced lithium battery storage for backup and peak shaving.</p>
                <div className="solution-card-features">
                  <span className="solution-feature">Lithium-ion</span>
                  <span className="solution-feature">Smart BMS</span>
                  <span className="solution-feature">10Y Life</span>
                </div>
                <Link to="/solutions" className="btn btn-outline btn-sm">Learn More <ChevronRight size={16} /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section section-darker">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><Settings size={14} /> Our Process</div>
            <h2 className="section-title">How We <span className="gold">Work</span></h2>
            <p className="section-description">A streamlined process designed for your convenience.</p>
          </div>

          <div className="process-timeline">
            <div className="process-step">
              <div className="process-step-number"><span>01</span></div>
              <div>
                <h5>Site Survey</h5>
                <p>Free technical assessment of your location</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-number"><span>02</span></div>
              <div>
                <h5>Custom Design</h5>
                <p>Optimized system design for maximum output</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-number"><span>03</span></div>
              <div>
                <h5>Installation</h5>
                <p>Professional installation by certified team</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-number"><span>04</span></div>
              <div>
                <h5>Commissioning</h5>
                <p>Testing, net metering & handover</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><FolderKanban size={14} /> Our Work</div>
            <h2 className="section-title">Featured <span className="gold">Projects</span></h2>
            <p className="section-description">Real installations. Real results. Real savings.</p>
          </div>

          <div className="projects-grid">
            <div className="project-card">
              <div className="project-card-image">
                <img src={IMAGES.commercial} alt="Commercial Project" />
              </div>
              <div className="project-card-overlay">
                <div className="project-card-meta">
                  <span>100 kW</span>
                  <span>Commercial</span>
                </div>
                <h4>Industrial Warehouse, PCMC</h4>
                <p>100kW rooftop installation with 40% energy cost reduction</p>
              </div>
            </div>

            <div className="project-card">
              <div className="project-card-image">
                <img src={IMAGES.rooftopSolar} alt="Residential Project" />
              </div>
              <div className="project-card-overlay">
                <div className="project-card-meta">
                  <span>10 kW</span>
                  <span>Residential</span>
                </div>
                <h4>Premium Villa, Koregaon Park</h4>
                <p>Complete energy independence with battery backup</p>
              </div>
            </div>

            <div className="project-card">
              <div className="project-card-image">
                <img src={IMAGES.solarField} alt="Ground Mount Project" />
              </div>
              <div className="project-card-overlay">
                <div className="project-card-meta">
                  <span>500 kW</span>
                  <span>Industrial</span>
                </div>
                <h4>Manufacturing Unit, Chakan</h4>
                <p>Ground-mounted solar farm for manufacturing facility</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-5">
            <Link to="/projects" className="btn btn-primary">
              View All Projects <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section section-darker">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><Quote size={14} /> Testimonials</div>
            <h2 className="section-title">Client <span className="gold">Success Stories</span></h2>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">
                {[1, 2, 3, 4, 5].map((starId) => <Star key={`star-1-${starId}`} size={18} fill="currentColor" />)}
              </div>
              <p className="testimonial-text">"AMROX delivered exceptional quality. Our 50kW system is generating 20% more than promised. Highly professional team."</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">RK</div>
                <div className="testimonial-author-info">
                  <h5>Rajesh Kumar</h5>
                  <p>Factory Owner, Bhosari</p>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                {[1, 2, 3, 4, 5].map((starId) => <Star key={`star-2-${starId}`} size={18} fill="currentColor" />)}
              </div>
              <p className="testimonial-text">"From site survey to commissioning, everything was seamless. Zero electricity bills now. Best investment ever!"</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">SM</div>
                <div className="testimonial-author-info">
                  <h5>Sunita Mehta</h5>
                  <p>Homeowner, Baner</p>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                {[1, 2, 3, 4, 5].map((starId) => <Star key={`star-3-${starId}`} size={18} fill="currentColor" />)}
              </div>
              <p className="testimonial-text">"AMROX handled our 200kW project with precision. On-time delivery and excellent after-sales support."</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">AP</div>
                <div className="testimonial-author-info">
                  <h5>Amit Patil</h5>
                  <p>MD, Tech Solutions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="calculator-container" style={{background: 'linear-gradient(135deg, rgba(201,163,78,0.1) 0%, rgba(0,87,255,0.1) 100%)'}}>
            <div className="calculator-form">
              <h3>Ready to Go Solar?</h3>
              <p className="text-grey mb-4">Get a free site survey and customized proposal for your property. Our experts will design the perfect system for maximum savings.</p>
              <div style={{display: 'flex', gap: '16px', flexWrap: 'wrap'}}>
                <Link to="/contact" className="btn btn-primary">
                  Book Free Site Survey <ArrowRight size={18} />
                </Link>
                <a href="tel:8805859200" className="btn btn-secondary">
                  <Phone size={18} /> Call Now
                </a>
              </div>
            </div>
            <div className="calculator-results" style={{background: 'transparent', padding: 0}}>
              <img src={IMAGES.engineers} alt="AMROX Team" style={{width: '100%', borderRadius: '16px', height: '300px', objectFit: 'cover'}} />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section - Placeholders */}
      <section className="section section-darker">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><Shield size={14} /> Trust & Compliance</div>
            <h2 className="section-title">Certifications & <span className="gold">Credentials</span></h2>
          </div>

          <div className="why-grid">
            <div className="why-card" style={{opacity: 0.7}}>
              <div className="why-card-icon"><FileText size={36} /></div>
              <h4>GST Registered</h4>
              <p>GST Number: [To be updated]</p>
            </div>
            <div className="why-card" style={{opacity: 0.7}}>
              <div className="why-card-icon"><Building2 size={36} /></div>
              <h4>CIN</h4>
              <p>Corporate ID: [To be updated]</p>
            </div>
            <div className="why-card" style={{opacity: 0.7}}>
              <div className="why-card-icon"><Award size={36} /></div>
              <h4>Certifications</h4>
              <p>ISO & MNRE Certified [To be updated]</p>
            </div>
            <div className="why-card" style={{opacity: 0.7}}>
              <div className="why-card-icon"><Star size={36} /></div>
              <h4>Google Reviews</h4>
              <p>4.9★ Rating [Link to be added]</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ ABOUT PAGE ============
const AboutPage = () => (
  <div style={{paddingTop: '80px'}} data-testid="about-page">
    <section className="section section-dark">
      <div className="container">
        <div className="section-header">
          <div className="section-badge"><Award size={14} /> About Us</div>
          <h2 className="section-title">About <span className="gold">AMROX ENERGY</span></h2>
          <p className="section-description">Powering Authority. Enabling Growth.</p>
        </div>

        <div className="about-grid">
          <div className="about-image">
            <img src={IMAGES.about} alt="AMROX Energy" />
          </div>
          <div className="about-content">
            <h3>Our Vision</h3>
            <p>AMROX ENERGY is a premium energy infrastructure brand delivering intelligent, scalable, and performance-driven solar and power solutions for residential, commercial, and industrial clients across Maharashtra.</p>
            <p>Under Amro Power Systems Pvt Ltd, we combine engineering excellence with cutting-edge technology to deliver solar EPC projects that maximize ROI and minimize environmental impact.</p>
            
            <h3 className="mt-4">Why We're Different</h3>
            <div className="about-features">
              <div className="about-feature">
                <div className="about-feature-icon"><CheckCircle size={24} /></div>
                <div>
                  <h5>Engineering Excellence</h5>
                  <p>Certified team with 10+ years experience</p>
                </div>
              </div>
              <div className="about-feature">
                <div className="about-feature-icon"><CheckCircle size={24} /></div>
                <div>
                  <h5>Premium Components</h5>
                  <p>Only Tier-1 panels and inverters</p>
                </div>
              </div>
              <div className="about-feature">
                <div className="about-feature-icon"><CheckCircle size={24} /></div>
                <div>
                  <h5>End-to-End Service</h5>
                  <p>Design to maintenance under one roof</p>
                </div>
              </div>
              <div className="about-feature">
                <div className="about-feature-icon"><CheckCircle size={24} /></div>
                <div>
                  <h5>Performance Guarantee</h5>
                  <p>Guaranteed generation output</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

// ============ SOLUTIONS PAGE ============
const SolutionsPage = () => {
  const [filter, setFilter] = useState('all');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get('/api/products').then(res => setProducts(res.data)).catch(() => {});
  }, []);

  const categories = [
    { id: 'all', label: 'All Solutions' },
    { id: 'ongrid', label: 'On-Grid' },
    { id: 'hybrid', label: 'Hybrid' },
    { id: 'offgrid', label: 'Off-Grid' },
    { id: 'lithium_batteries', label: 'Battery Storage' },
    { id: 'inverters', label: 'Inverters' },
  ];

  const filteredProducts = filter === 'all' ? products : products.filter(p => p.category === filter);

  return (
    <div style={{paddingTop: '80px'}} data-testid="solutions-page">
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><Sun size={14} /> Solutions</div>
            <h2 className="section-title">Our <span className="gold">Solutions</span></h2>
            <p className="section-description">Premium solar systems and energy storage solutions for every need.</p>
          </div>

          <div className="solutions-tabs">
            {categories.map(cat => (
              <button 
                key={cat.id}
                className={`solutions-tab ${filter === cat.id ? 'active' : ''}`}
                onClick={() => setFilter(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="solutions-grid">
            {filteredProducts.map(product => (
              <div key={product.id} className="solution-card">
                <div className="solution-card-image">
                  <img src={product.image_url || IMAGES.rooftopSolar} alt={product.name} />
                  <span className="solution-card-badge">{product.category?.replace('_', ' ')}</span>
                </div>
                <div className="solution-card-content">
                  <h4>{product.name}</h4>
                  <p>{product.description?.substring(0, 100)}...</p>
                  {product.price && (
                    <p className="text-gold" style={{fontWeight: 700, fontSize: '1.25rem'}}>
                      ₹{product.price?.toLocaleString()}
                    </p>
                  )}
                  <Link to="/contact" className="btn btn-outline btn-sm mt-2">Get Quote <ChevronRight size={16} /></Link>
                </div>
              </div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="empty-state">
              <Package size={64} />
              <p>No products found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// ============ PROJECTS PAGE ============
const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/api/projects').then(res => setProjects(res.data)).catch(() => {});
  }, []);

  const types = ['all', 'residential', 'commercial', 'industrial'];
  const filteredProjects = filter === 'all' ? projects : projects.filter(p => p.system_type === filter);

  return (
    <div style={{paddingTop: '80px'}} data-testid="projects-page">
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><FolderKanban size={14} /> Projects</div>
            <h2 className="section-title">Our <span className="gold">Projects</span></h2>
            <p className="section-description">850+ successful installations across Maharashtra.</p>
          </div>

          <div className="tabs" style={{justifyContent: 'center'}}>
            {types.map(t => (
              <button key={t} className={`tab-btn ${filter === t ? 'active' : ''}`} onClick={() => setFilter(t)}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          <div className="projects-grid">
            {filteredProjects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-card-image">
                  <img src={project.image_url || IMAGES.commercial} alt={project.name} />
                </div>
                <div className="project-card-overlay">
                  <div className="project-card-meta">
                    <span>{project.capacity}</span>
                    <span>{project.system_type}</span>
                  </div>
                  <h4>{project.name}</h4>
                  <p>{project.location}</p>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="empty-state">
              <FolderKanban size={64} />
              <p>No projects found.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// ============ CALCULATOR PAGE ============
const CalculatorPage = () => {
  const [form, setForm] = useState({ monthlyBill: 3000, electricityRate: 8, roofArea: 500 });
  const [result, setResult] = useState(null);

  const calculate = () => {
    const monthlyUnits = form.monthlyBill / form.electricityRate;
    const annualUnits = monthlyUnits * 12;
    const systemSize = Math.ceil(annualUnits / ANNUAL_GENERATION_PER_KW);
    const systemCost = systemSize * SYSTEM_COST_PER_KW;
    const annualSavings = annualUnits * form.electricityRate;
    const payback = Math.round(systemCost / annualSavings * 10) / 10;
    const lifetimeSavings = annualSavings * SYSTEM_LIFETIME_YEARS - systemCost;
    
    setResult({
      systemSize,
      systemCost,
      annualGeneration: annualUnits,
      annualSavings,
      payback,
      lifetimeSavings,
      co2Offset: Math.round(annualUnits * CO2_OFFSET_FACTOR / 1000 * SYSTEM_LIFETIME_YEARS)
    });
  };

  return (
    <div style={{paddingTop: '80px'}} data-testid="calculator-page">
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><Calculator size={14} /> Calculator</div>
            <h2 className="section-title">Solar <span className="gold">Savings Calculator</span></h2>
            <p className="section-description">Calculate your potential savings with solar energy.</p>
          </div>

          <div className="calculator-container">
            <div className="calculator-form">
              <h3>Enter Your Details</h3>
              <div className="form-group">
                <label>Monthly Electricity Bill (₹)</label>
                <input 
                  type="number" 
                  value={form.monthlyBill} 
                  onChange={e => setForm({...form, monthlyBill: Number(e.target.value)})}
                  placeholder="Enter monthly bill"
                />
              </div>
              <div className="form-group">
                <label>Electricity Rate (₹/unit)</label>
                <input 
                  type="number" 
                  value={form.electricityRate} 
                  onChange={e => setForm({...form, electricityRate: Number(e.target.value)})}
                  placeholder="Enter rate per unit"
                />
              </div>
              <div className="form-group">
                <label>Available Roof Area (sq.ft)</label>
                <input 
                  type="number" 
                  value={form.roofArea} 
                  onChange={e => setForm({...form, roofArea: Number(e.target.value)})}
                  placeholder="Enter roof area"
                />
              </div>
              <button className="btn btn-primary" onClick={calculate} style={{width: '100%'}}>
                Calculate Savings <Calculator size={18} />
              </button>
            </div>

            <div className="calculator-results">
              <h3>Your Savings Estimate</h3>
              {result ? (
                <>
                  <div className="result-item">
                    <span className="result-label">Recommended System Size</span>
                    <span className="result-value gold">{result.systemSize} kW</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Estimated System Cost</span>
                    <span className="result-value">₹{result.systemCost.toLocaleString()}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Annual Generation</span>
                    <span className="result-value">{result.annualGeneration.toLocaleString()} kWh</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Annual Savings</span>
                    <span className="result-value gold">₹{result.annualSavings.toLocaleString()}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Payback Period</span>
                    <span className="result-value">{result.payback} years</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">25-Year Savings</span>
                    <span className="result-value gold">₹{result.lifetimeSavings.toLocaleString()}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">CO₂ Offset (25 years)</span>
                    <span className="result-value">{result.co2Offset} tons</span>
                  </div>
                  <Link to="/contact" className="btn btn-primary mt-4" style={{width: '100%'}}>
                    Get Free Site Survey <ArrowRight size={18} />
                  </Link>
                </>
              ) : (
                <div className="empty-state" style={{padding: '40px 20px'}}>
                  <Calculator size={48} />
                  <p>Enter your details and click calculate to see your potential savings.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ CONTACT PAGE ============
const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', type: 'residential' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/api/leads/public', form);
      toast.success('Thank you! Our team will contact you within 24 hours.');
      setForm({ name: '', email: '', phone: '', message: '', type: 'residential' });
    } catch {
      toast.error('Failed to submit. Please try again or call us directly.');
    }
    setLoading(false);
  };

  return (
    <div style={{paddingTop: '80px'}} data-testid="contact-page">
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge"><Phone size={14} /> Contact</div>
            <h2 className="section-title">Get in <span className="gold">Touch</span></h2>
            <p className="section-description">Request a free site survey or talk to our technical experts.</p>
          </div>

          <div className="contact-grid">
            <div className="contact-info">
              <h3>Contact Information</h3>
              <p className="text-grey">Reach out to us for a free consultation and site survey. Our experts are ready to help you switch to solar.</p>

              <div className="contact-cards">
                <div className="contact-card">
                  <div className="contact-card-icon"><Phone size={24} /></div>
                  <div>
                    <h5>Sales & Technical</h5>
                    <p><a href={`tel:${CONTACT.salesPhone}`}>{CONTACT.salesPhone}</a></p>
                  </div>
                </div>
                <div className="contact-card">
                  <div className="contact-card-icon"><Phone size={24} /></div>
                  <div>
                    <h5>Service Escalation</h5>
                    <p><a href={`tel:${CONTACT.escalationPhone}`}>{CONTACT.escalationPhone}</a></p>
                  </div>
                </div>
                <div className="contact-card">
                  <div className="contact-card-icon"><Mail size={24} /></div>
                  <div>
                    <h5>Email</h5>
                    <p><a href={`mailto:${CONTACT.email1}`}>{CONTACT.email1}</a></p>
                    <p><a href={`mailto:${CONTACT.email2}`}>{CONTACT.email2}</a></p>
                  </div>
                </div>
                <div className="contact-card">
                  <div className="contact-card-icon"><MapPin size={24} /></div>
                  <div>
                    <h5>Service Area</h5>
                    <p>Pune, Pimpri-Chinchwad, Maharashtra</p>
                  </div>
                </div>
                <div className="contact-card">
                  <div className="contact-card-icon"><Clock size={24} /></div>
                  <div>
                    <h5>Working Hours</h5>
                    <p>Mon - Sat: 9:00 AM - 7:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <h3>Request a Free Site Survey</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Your Name *</label>
                    <input 
                      type="text" 
                      required 
                      value={form.name}
                      onChange={e => setForm({...form, name: e.target.value})}
                      placeholder="Enter your name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input 
                      type="tel" 
                      required 
                      value={form.phone}
                      onChange={e => setForm({...form, phone: e.target.value})}
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    value={form.email}
                    onChange={e => setForm({...form, email: e.target.value})}
                    placeholder="Enter email address"
                  />
                </div>
                <div className="form-group">
                  <label>Project Type</label>
                  <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea 
                    rows={4}
                    value={form.message}
                    onChange={e => setForm({...form, message: e.target.value})}
                    placeholder="Tell us about your requirements..."
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading} style={{width: '100%'}}>
                  {loading ? 'Submitting...' : 'Submit Request'} <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ ADMIN LOGIN ============
const AdminLogin = () => {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/api/admin/login', form);
      localStorage.setItem('admin_token', res.data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(res.data.user));
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch {
      toast.error('Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="admin-login" data-testid="admin-login">
      <div className="admin-login-card">
        <div style={{textAlign: 'center', marginBottom: '24px'}}>
          <img src={IMAGES.logo} alt="AMROX" style={{height: '60px', marginBottom: '16px'}} />
          <p className="text-grey">Sign in to access the admin panel</p>
        </div>
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Username</label>
            <input 
              type="text" 
              required
              data-testid="username-input"
              value={form.username}
              onChange={e => setForm({...form, username: e.target.value})}
              placeholder="Enter username"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input 
              type="password" 
              required
              data-testid="password-input"
              value={form.password}
              onChange={e => setForm({...form, password: e.target.value})}
              placeholder="Enter password"
            />
          </div>
          <button type="submit" className="btn btn-primary" data-testid="login-btn" disabled={loading} style={{width: '100%'}}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============ ADMIN LAYOUT ============
const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) navigate('/admin');
  }, [navigate]);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin');
  };

  return (
    <div style={{display: 'flex', background: 'var(--black)', minHeight: '100vh'}}>
      <div className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <img src={IMAGES.logo} alt="AMROX" style={{height: '45px', marginBottom: '8px'}} />
          <span className="navbar-brand-sub" style={{fontSize: '0.7rem'}}>Admin Panel</span>
        </div>
        <nav className="admin-sidebar-nav">
          <Link to="/admin/dashboard" className={isActive('/admin/dashboard') ? 'active' : ''}>
            <LayoutDashboard size={20} /> Dashboard
          </Link>
          <Link to="/admin/leads" className={isActive('/admin/leads') ? 'active' : ''}>
            <Users size={20} /> Leads
          </Link>
          <Link to="/admin/products" className={isActive('/admin/products') ? 'active' : ''}>
            <Package size={20} /> Products
          </Link>
          <Link to="/admin/projects" className={isActive('/admin/projects') ? 'active' : ''}>
            <FolderKanban size={20} /> Projects
          </Link>
          <Link to="/admin/reviews" className={isActive('/admin/reviews') ? 'active' : ''}>
            <Star size={20} /> Reviews
          </Link>
          <Link to="/admin/quotations" className={isActive('/admin/quotations') ? 'active' : ''}>
            <FileText size={20} /> Quotations
          </Link>
          <Link to="/admin/gallery" className={isActive('/admin/gallery') ? 'active' : ''}>
            <Image size={20} /> Gallery
          </Link>
          <Link to="/" style={{marginTop: 'auto'}}>
            <Eye size={20} /> View Site
          </Link>
          <button onClick={handleLogout} style={{background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', width: '100%', textAlign: 'left'}}>
            <LogOut size={20} /> Logout
          </button>
        </nav>
      </div>
      <div className="admin-content">
        {children}
      </div>
    </div>
  );
};

// ============ ADMIN DASHBOARD ============
const AdminDashboard = () => {
  const [stats, setStats] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    api.get('/api/admin/analytics', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setStats(res.data))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="admin-header">
        <h1>Dashboard</h1>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon"><Users size={24} /></div>
          <h3>{stats.total_leads || 0}</h3>
          <p className="text-grey">Total Leads</p>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><Package size={24} /></div>
          <h3>{stats.total_products || 0}</h3>
          <p className="text-grey">Products</p>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><FolderKanban size={24} /></div>
          <h3>{stats.total_projects || 0}</h3>
          <p className="text-grey">Projects</p>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><FileText size={24} /></div>
          <h3>{stats.total_quotations || 0}</h3>
          <p className="text-grey">Quotations</p>
        </div>
      </div>
    </div>
  );
};

// ============ ADMIN LEADS ============
const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    api.get('/api/admin/leads', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setLeads(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    const token = localStorage.getItem('admin_token');
    await api.delete(`/api/admin/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Lead deleted');
    fetchLeads();
  };

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-leads">
      <div className="admin-header">
        <h1>Leads</h1>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Type</th>
              <th>Message</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map(lead => (
              <tr key={lead.id}>
                <td>{lead.name}</td>
                <td>{lead.phone}<br/><span className="text-grey">{lead.email}</span></td>
                <td>{lead.type || 'N/A'}</td>
                <td>{lead.message?.substring(0, 50)}...</td>
                <td>{new Date(lead.created_at).toLocaleDateString()}</td>
                <td>
                  <button className="action-btn delete" onClick={() => deleteLead(lead.id)}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {leads.length === 0 && (
        <div className="empty-state">
          <Users size={48} />
          <p>No leads yet.</p>
        </div>
      )}
    </div>
  );
};

// ============ ADMIN PRODUCTS ============
const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const fetchProducts = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    api.get('/api/admin/products', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setProducts(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    const token = localStorage.getItem('admin_token');
    await api.delete(`/api/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Product deleted');
    fetchProducts();
  };

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-products">
      <div className="admin-header">
        <h1>Products</h1>
        <button className="btn btn-primary" onClick={() => { setEditProduct(null); setShowModal(true); }}>
          <Plus size={18} /> Add Product
        </button>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.id}>
                <td>{product.name}</td>
                <td>{product.category}</td>
                <td>₹{product.price?.toLocaleString() || 'N/A'}</td>
                <td style={{display: 'flex', gap: '8px'}}>
                  <button className="action-btn" onClick={() => { setEditProduct(product); setShowModal(true); }}>
                    <Edit size={16} />
                  </button>
                  <button className="action-btn delete" onClick={() => deleteProduct(product.id)}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <ProductModal 
          product={editProduct} 
          onClose={() => setShowModal(false)} 
          onSave={() => { setShowModal(false); fetchProducts(); }} 
        />
      )}
    </div>
  );
};

// Product Modal
const ProductModal = ({ product, onClose, onSave }) => {
  const [form, setForm] = useState(product || {
    name: '', category: 'ongrid', price: '', description: '', image_url: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    try {
      if (product) {
        await api.put(`/api/admin/products/${product.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Product updated');
      } else {
        await api.post('/api/admin/products', form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Product created');
      }
      onSave();
    } catch {
      toast.error('Failed to save product');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{product ? 'Edit Product' : 'Add Product'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Product Name *</label>
                <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                  <option value="ongrid">On-Grid</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="offgrid">Off-Grid</option>
                  <option value="lithium_batteries">Lithium Batteries</option>
                  <option value="inverters">Inverters</option>
                  <option value="online_ups">Online UPS</option>
                </select>
              </div>
              <div className="form-group">
                <label>Price (₹)</label>
                <input type="number" value={form.price} onChange={e => setForm({...form, price: Number(e.target.value)})} />
              </div>
              <div className="form-group full-width">
                <label>Image URL</label>
                <input type="url" value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} />
              </div>
              <div className="form-group full-width">
                <label>Description</label>
                <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ ADMIN PROJECTS ============
const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    api.get('/api/admin/projects', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setProjects(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-projects">
      <div className="admin-header">
        <h1>Projects</h1>
        <button className="btn btn-primary"><Plus size={18} /> Add Project</button>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Capacity</th>
              <th>Type</th>
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {projects.map(project => (
              <tr key={project.id}>
                <td>{project.name}</td>
                <td>{project.capacity}</td>
                <td>{project.system_type}</td>
                <td>{project.location}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ ADMIN REVIEWS ============
const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    api.get('/api/admin/reviews', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setReviews(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-reviews">
      <div className="admin-header">
        <h1>Reviews</h1>
        <button className="btn btn-primary"><Plus size={18} /> Add Review</button>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Author</th>
              <th>Rating</th>
              <th>Review</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map(review => (
              <tr key={review.id}>
                <td>{review.author}</td>
                <td>{review.rating}★</td>
                <td>{review.text?.substring(0, 60)}...</td>
                <td>{review.approved ? 'Approved' : 'Pending'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ ADMIN QUOTATIONS ============
const AdminQuotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchQuotations = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    api.get('/api/admin/quotations', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setQuotations(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchQuotations(); }, [fetchQuotations]);

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-quotations">
      <div className="admin-header">
        <h1>Quotations</h1>
        <button className="btn btn-primary"><Plus size={18} /> Create Quotation</button>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Quote #</th>
              <th>Client</th>
              <th>System</th>
              <th>Total</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {quotations.map(quote => (
              <tr key={quote.id}>
                <td>{quote.quotation_number}</td>
                <td>{quote.client?.name}</td>
                <td>{quote.system_capacity}kW {quote.system_type}</td>
                <td>₹{quote.total_cost?.toLocaleString()}</td>
                <td>{new Date(quote.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ ADMIN GALLERY ============
const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const fetchItems = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    api.get('/api/admin/gallery', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setItems(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this gallery item?')) return;
    const token = localStorage.getItem('admin_token');
    await api.delete(`/api/admin/gallery/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Item deleted');
    fetchItems();
  };

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-gallery">
      <div className="admin-header">
        <h1>Gallery</h1>
        <button className="btn btn-primary" onClick={() => { setEditItem(null); setShowModal(true); }}>
          <Plus size={18} /> Add Photo
        </button>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px'}}>
        {items.map(item => (
          <div key={item.id} className="data-table-container" style={{padding: 0, overflow: 'hidden'}}>
            <div style={{height: '180px', overflow: 'hidden'}}>
              <img src={item.image_url} alt={item.title} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
            </div>
            <div style={{padding: '16px'}}>
              <h5 style={{marginBottom: '4px'}}>{item.title}</h5>
              <p className="text-grey" style={{fontSize: '0.85rem', marginBottom: '12px'}}>{item.category}</p>
              <div style={{display: 'flex', gap: '8px'}}>
                <button className="action-btn" onClick={() => { setEditItem(item); setShowModal(true); }}>
                  <Edit size={16} />
                </button>
                <button className="action-btn delete" onClick={() => deleteItem(item.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="empty-state">
          <Image size={48} />
          <p>No gallery items yet.</p>
        </div>
      )}

      {showModal && (
        <GalleryModal 
          item={editItem} 
          onClose={() => setShowModal(false)} 
          onSave={() => { setShowModal(false); fetchItems(); }} 
        />
      )}
    </div>
  );
};

// Gallery Modal
const GalleryModal = ({ item, onClose, onSave }) => {
  const [form, setForm] = useState(item || {
    title: '', description: '', image_url: '', category: 'project', is_featured: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    try {
      if (item) {
        await api.put(`/api/admin/gallery/${item.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Gallery item updated');
      } else {
        await api.post('/api/admin/gallery', form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Gallery item created');
      }
      onSave();
    } catch {
      toast.error('Failed to save');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{item ? 'Edit Gallery Item' : 'Add Gallery Item'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Title *</label>
              <input type="text" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Image URL *</label>
              <input type="url" required value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                <option value="project">Project</option>
                <option value="installation">Installation</option>
                <option value="promotional">Promotional</option>
                <option value="general">General</option>
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ APP ROUTER ============
function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<><Navbar /><HomePage /><Footer /><WhatsAppButton /><StickyCTA /></>} />
        <Route path="/about" element={<><Navbar /><AboutPage /><Footer /><WhatsAppButton /></>} />
        <Route path="/solutions" element={<><Navbar /><SolutionsPage /><Footer /><WhatsAppButton /></>} />
        <Route path="/projects" element={<><Navbar /><ProjectsPage /><Footer /><WhatsAppButton /></>} />
        <Route path="/calculator" element={<><Navbar /><CalculatorPage /><Footer /><WhatsAppButton /></>} />
        <Route path="/contact" element={<><Navbar /><ContactPage /><Footer /><WhatsAppButton /></>} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
        <Route path="/admin/leads" element={<AdminLayout><AdminLeads /></AdminLayout>} />
        <Route path="/admin/products" element={<AdminLayout><AdminProducts /></AdminLayout>} />
        <Route path="/admin/projects" element={<AdminLayout><AdminProjects /></AdminLayout>} />
        <Route path="/admin/reviews" element={<AdminLayout><AdminReviews /></AdminLayout>} />
        <Route path="/admin/quotations" element={<AdminLayout><AdminQuotations /></AdminLayout>} />
        <Route path="/admin/gallery" element={<AdminLayout><AdminGallery /></AdminLayout>} />
      </Routes>
      <Toaster position="top-right" richColors theme="dark" />
    </BrowserRouter>
  );
}

export default App;
